<template>
  <OverlayElement :visible @click="hide" />
  <div class="burger" :class="{ 'burger--visible': visible }" :inert="!visible">
    <div class="burger__close" title="Fermer ce menu" @click="hide">
      <PhX />
    </div>

    <menu class="burger__menu">
      <!-- Menu connecté -->
      <template v-if="userStore.isAuthenticated">
        <div class="burger__section-title">
          <span>Production</span>
        </div>

        <li class="burger__item burger__item--clickable">
          <PhScroll class="burger__icon" />
          <router-link :to="{ name: 'ordres-fabrication' }" class="burger__label">
            Ordres de fabrication
          </router-link>
        </li>

        <!-- Menus admin -->
        <template v-if="userStore.isAdmin">
          <div class="burger__section-title">
            <span>Administration</span>
          </div>

          <li class="burger__item burger__item--clickable">
            <PhUsersThree class="burger__icon" />
            <router-link :to="{ name: 'utilisateurs' }" class="burger__label">
              Utilisateurs
            </router-link>
          </li>

          <li class="burger__item burger__item--clickable">
            <PhTruck class="burger__icon" />
            <router-link :to="{ name: 'equipements' }" class="burger__label">
              Equipements
            </router-link>
          </li>

          <li class="burger__item burger__item--clickable">
            <PhFunnel class="burger__icon" />
            <router-link :to="{ name: 'produits-filtration' }" class="burger__label">
              Produits de filtration
            </router-link>
          </li>

          <li class="burger__item burger__item--clickable">
            <PhFactory class="burger__icon" />
            <router-link :to="{ name: 'fabricants' }" class="burger__label">
              Fabricants
            </router-link>
          </li>

          <li class="burger__item burger__item--clickable">
            <PhFolderSimpleUser class="burger__icon" />
            <router-link :to="{ name: 'clients' }" class="burger__label"> Clients </router-link>
          </li>
        </template>

        <div class="burger__section-title">
          <span>Mon espace</span>
        </div>

        <li class="burger__item burger__item--clickable">
          <PhUser class="burger__icon" />
          <router-link :to="{ name: 'mon-compte' }" class="burger__label"> Mon compte </router-link>
        </li>

        <li class="burger__item burger__item--clickable">
          <PhSignOut class="burger__icon" />
          <span class="burger__label" @click="logOut"> Déconnexion </span>
        </li>
      </template>

      <!-- Menu pas connecté -->
      <template v-else>
        <li class="burger__item burger__item--clickable">
          <PhSignIn class="burger__icon" />
          <router-link :to="{ name: 'connexion' }" class="burger__label"> Connexion </router-link>
        </li>
      </template>
    </menu>

    <menu class="burger__menu">
      <li
        class="burger__item burger__item--clickable"
        @click="update"
        v-if="needRefresh || !isSameVersion"
      >
        <PhDownloadSimple class="burger__icon" />
        <span class="burger__label">Nouvelle version disponible</span>
      </li>

      <li class="burger__item">
        <component :is="cacheStore.online ? PhWifiHigh : PhWifiSlash" class="burger__icon" />
        <span class="burger__label"> {{ cacheStore.online ? 'Connecté' : 'Hors-ligne' }} </span>
      </li>

      <li class="burger__item" v-if="version">
        <PhGitBranch class="burger__icon" />
        <span class="burger__label burger__label--monospace">
          {{ version }}
        </span>
      </li>
    </menu>
  </div>
</template>

<script setup lang="ts">
import {
  PhDownloadSimple,
  PhFactory,
  PhFolderSimpleUser,
  PhFunnel,
  PhGitBranch,
  PhScroll,
  PhSignIn,
  PhSignOut,
  PhTruck,
  PhUser,
  PhUsersThree,
  PhWifiHigh,
  PhWifiSlash,
  PhX,
} from '@phosphor-icons/vue'
import { useUserStore } from '@/stores/user.ts'
import { useToastStore } from '@/stores/toast.ts'
import { useRoute, useRouter } from 'vue-router'
import { computed, onMounted, ref, type Ref, watch } from 'vue'
import useFetch from '@/composables/useFetch.ts'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import OverlayElement from '@/components/OverlayElement.vue'
import { useCacheStore } from '@/stores/cache.ts'

// dépendances
const api = useFetch()
const router = useRouter()
const route = useRoute()

// stores
const userStore = useUserStore()
const toastStore = useToastStore()
const cacheStore = useCacheStore()

// env
const version = import.meta.env.VITE_VERSION

// données
const backVersion: Ref<string | undefined> = ref(undefined)
const { needRefresh, updateServiceWorker } = useRegisterSW()

// computed
const isSameVersion = computed(
  () => backVersion.value !== undefined && version === backVersion.value,
)

// modèle
const visible = defineModel<boolean>({ required: true })

// fonctions
function hide() {
  visible.value = false
}
async function logOut() {
  await userStore.logOut()
  hide()
  toastStore.success('Vous êtes déconnecté.')
  await router.push({ name: 'connexion' })
}
async function getVersion() {
  backVersion.value = await api.get('version/')
}
function update() {
  const value = confirm(
    "Souhaitez-vous mettre à jour l'application maintenant ?\nToute modification non-enregistrée sera perdue !",
  )
  if (value) {
    updateServiceWorker()
    window.location.reload()
  }
}

addEventListener('keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape' && visible.value) hide()
})

watch(
  () => route.name,
  () => hide(),
)

onMounted(async () => {
  // on récupère la version du back
  await getVersion()
  setInterval(async () => {
    await getVersion()
  }, 900000)
})
</script>
